'use client';
import React, { useMemo, useState } from 'react';
import { BacktestOutput } from '@/lib/backtest';
import { PillarWeights, DEFAULT_WEIGHTS, recomputeBacktest, VintageStockData } from '@/lib/clientBacktest';
import { factorLabel, QUALITY_FACTORS, VALUATION_FACTORS } from '@/lib/clientScore';
import { StockFactors } from '@/lib/compute';
import { AlphaVsRestBacktest } from './AlphaVsRestBacktest';

interface Props {
  data: BacktestOutput;
}

// ─── IC Color ────────────────────────────────────────────────────────────────

function icColor(ic: number | null): string {
  if (ic === null) return '#1e293b';
  if (ic > 0.15) return '#166534';
  if (ic > 0.08) return '#15803d';
  if (ic > 0.03) return '#1a3a2a';
  if (ic > -0.03) return '#1e293b';
  if (ic > -0.08) return '#3a2a1a';
  if (ic > -0.15) return '#9a3412';
  return '#991b1b';
}

function icText(ic: number | null): string {
  if (ic === null) return 'N/A';
  return (ic >= 0 ? '+' : '') + ic.toFixed(3);
}

function icTextColor(ic: number | null): string {
  if (ic === null) return '#475569';
  if (ic > 0.08) return '#4ade80';
  if (ic > 0.03) return '#86efac';
  if (ic > -0.03) return '#94a3b8';
  if (ic > -0.08) return '#fdba74';
  return '#f87171';
}

// ─── Quintile Chart ──────────────────────────────────────────────────────────

function QuintileChart({ quintileReturns, avgQuintileReturns, vintages }: {
  quintileReturns: Record<string, number[]>;
  avgQuintileReturns: number[];
  vintages: string[];
}) {
  const W = 600;
  const H = 200;
  const PAD = { top: 20, right: 20, bottom: 30, left: 50 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const validVintages = vintages.filter(v => quintileReturns[v]?.length === 5);
  const allVals = [...avgQuintileReturns, ...validVintages.flatMap(v => quintileReturns[v])];
  if (allVals.length === 0) return <div className="text-white text-[10px] py-4">No quintile data available</div>;

  const maxVal = Math.max(...allVals.map(Math.abs), 0.01);
  const yRange = maxVal * 1.2;

  const groupW = chartW / 5;
  const barW = Math.min(12, groupW / (validVintages.length + 2));
  const totalBarsW = barW * (validVintages.length + 1);

  const toY = (v: number) => PAD.top + chartH / 2 - (v / yRange) * (chartH / 2);

  const vintageColors = ['#60a5fa', '#818cf8', '#f472b6', '#fbbf24'];

  return (
    <svg width={W} height={H} className="w-full" viewBox={`0 0 ${W} ${H}`}>
      {/* Zero line */}
      <line x1={PAD.left} y1={toY(0)} x2={PAD.left + chartW} y2={toY(0)}
        stroke="rgba(255,255,255,0.15)" strokeWidth={1} />

      {/* Y-axis ticks */}
      {[-0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3].filter(v => Math.abs(v) <= yRange).map(v => (
        <text key={v} x={PAD.left - 4} y={toY(v) + 3} fill="#475569" fontSize={8} textAnchor="end"
          fontFamily="var(--font-mono), monospace">
          {(v * 100).toFixed(0)}%
        </text>
      ))}

      {/* Bars per quintile */}
      {[0, 1, 2, 3, 4].map(q => {
        const cx = PAD.left + groupW * q + groupW / 2;
        const startX = cx - totalBarsW / 2;

        return (
          <g key={q}>
            {/* Vintage bars */}
            {validVintages.map((v, vi) => {
              const val = quintileReturns[v][q];
              const x = startX + vi * barW;
              const y = val >= 0 ? toY(val) : toY(0);
              const h = Math.abs(toY(val) - toY(0));
              return (
                <rect key={v} x={x} y={y} width={barW - 1} height={Math.max(1, h)}
                  fill={vintageColors[vi % vintageColors.length]} opacity={0.5} rx={1} />
              );
            })}
            {/* Average bar (bold) */}
            {avgQuintileReturns.length === 5 && (() => {
              const val = avgQuintileReturns[q];
              const x = startX + validVintages.length * barW;
              const y = val >= 0 ? toY(val) : toY(0);
              const h = Math.abs(toY(val) - toY(0));
              const color = val >= 0 ? '#22c55e' : '#ef4444';
              return (
                <g>
                  <rect x={x} y={y} width={barW - 1} height={Math.max(1, h)}
                    fill={color} opacity={0.9} rx={1} />
                  <text x={x + barW / 2} y={val >= 0 ? y - 3 : y + h + 9}
                    fill={color} fontSize={8} textAnchor="middle" fontWeight={700}
                    fontFamily="var(--font-mono), monospace">
                    {(val * 100).toFixed(1)}%
                  </text>
                </g>
              );
            })()}
            {/* Quintile label */}
            <text x={cx} y={H - 8} fill="#94a3b8" fontSize={9} textAnchor="middle"
              fontFamily="var(--font-mono), monospace" fontWeight={600}>
              Q{q + 1}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      {validVintages.map((v, i) => (
        <g key={v}>
          <rect x={PAD.left + i * 55} y={2} width={8} height={8} fill={vintageColors[i]} opacity={0.6} rx={1} />
          <text x={PAD.left + i * 55 + 11} y={9} fill="#64748b" fontSize={8}
            fontFamily="var(--font-mono), monospace">{v}</text>
        </g>
      ))}
      <rect x={PAD.left + validVintages.length * 55} y={2} width={8} height={8} fill="#22c55e" opacity={0.9} rx={1} />
      <text x={PAD.left + validVintages.length * 55 + 11} y={9} fill="#94a3b8" fontSize={8} fontWeight={600}
        fontFamily="var(--font-mono), monospace">Avg</text>
    </svg>
  );
}

// ─── Weight Slider ───────────────────────────────────────────────────────────

function WeightSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-[#e2e8f0] w-[80px] flex-shrink-0">{label}</span>
      <input type="range" min={0} max={3} step={0.1} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="flex-1 cursor-pointer" />
      <span className="text-[10px] text-white w-[28px] text-right tabular-nums"
        style={{ fontFamily: 'var(--font-mono)' }}>
        {value.toFixed(1)}x
      </span>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function BacktestView({ data }: Props) {
  const [weights, setWeights] = useState<PillarWeights>(DEFAULT_WEIGHTS);

  const hasVintageData = Boolean(data.vintages?.length && data.vintageStocks);

  // Recompute quintiles and composite IC when weights change
  const recomputed = useMemo(() => {
    if (!hasVintageData) return null;
    return recomputeBacktest(
      data.vintageStocks as Record<string, VintageStockData[]>,
      data.vintages,
      weights,
    );
  }, [data, weights, hasVintageData]);

  const isDefaultWeights = Object.entries(weights).every(([, v]) => v === 1);

  // Use recomputed data for quintiles/composite, original data for factor ICs
  const quintileReturns = isDefaultWeights ? (data.quintileReturns ?? {}) : (recomputed?.quintileReturns ?? {});
  const avgQuintileReturns = isDefaultWeights ? (data.avgQuintileReturns ?? []) : (recomputed?.avgQuintileReturns ?? []);
  const compositeIC = isDefaultWeights ? (data.avgCompositeIC ?? null) : (recomputed?.avgCompositeIC ?? null);

  const spread = avgQuintileReturns.length === 5
    ? avgQuintileReturns[0] - avgQuintileReturns[4]
    : null;

  // Build factor rows for IC heatmap
  const allPillars = Object.entries(QUALITY_FACTORS) as [string, { key: keyof StockFactors; invert: boolean }[]][];

  return (
    <div className="flex-1 overflow-auto p-6 pl-10" style={{ background: '#0a0c10' }}>
      <div className="max-w-[900px] mx-auto space-y-6">

        {/* Headline: Alpha Zone vs Rest of Universe — the only comparison most
            people actually want. Sources from data/score-return-curve.json
            (vintage-by-vintage, forward 12m, no analyst peeking). */}
        <AlphaVsRestBacktest />

        {/* Legacy quadrant + factor IC views below — kept for reference but
            secondary. The quadrant medians bucket on TODAY's score against
            trailing return, which has a known reverse-causality bias. */}
        <div className="pt-6 border-t border-[#1e293b]">
          <h2 className="text-[#94a3b8] text-lg font-semibold">Factor Backtest (legacy)</h2>
          <p className="text-[#e2e8f0] text-xs mt-1">
            {hasVintageData
              ? `Historical factor efficacy across ${data.vintages.length} annual vintages (${data.vintages.join(', ')}). Scores computed using trailing fundamentals only (no analyst estimates). Current S&P 500 universe — survivorship bias applies.`
              : 'Quadrant medians below bucket the universe by today’s Q/V score and measure trailing 1Y/3Y/5Y price return. Survivorship and reverse-causality bias apply: a stock that rallied is now expensive, so the "More Expensive" quadrant inherits big trailing winners by construction. Use the headline Alpha-vs-Rest panel above for the forward-looking number.'}
          </p>
        </div>

        {/* Summary Stats */}
        {hasVintageData ? (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg p-3 bg-[#0d1117] border border-[#1e293b]">
            <div className="text-[9px] text-[#e2e8f0] uppercase tracking-wider mb-1">Composite IC</div>
            <div className="text-lg font-bold tabular-nums"
              style={{ color: icTextColor(compositeIC), fontFamily: 'var(--font-mono)' }}>
              {compositeIC !== null ? compositeIC.toFixed(3) : 'N/A'}
            </div>
          </div>
          <div className="rounded-lg p-3 bg-[#0d1117] border border-[#1e293b]">
            <div className="text-[9px] text-[#e2e8f0] uppercase tracking-wider mb-1">Q1-Q5 Spread</div>
            <div className="text-lg font-bold tabular-nums"
              style={{ color: spread && spread > 0 ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>
              {spread !== null ? `${(spread * 100).toFixed(1)}%` : 'N/A'}
            </div>
          </div>
          <div className="rounded-lg p-3 bg-[#0d1117] border border-[#1e293b]">
            <div className="text-[9px] text-[#e2e8f0] uppercase tracking-wider mb-1">Q1 Avg Return</div>
            <div className="text-lg font-bold tabular-nums"
              style={{ color: avgQuintileReturns[0] > 0 ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>
              {avgQuintileReturns.length > 0 ? `${(avgQuintileReturns[0] * 100).toFixed(1)}%` : 'N/A'}
            </div>
          </div>
        </div>
        ) : data.periods && (
        <div>
          {Object.entries(data.periods).map(([key, pd]) => (
            <div key={key} className="mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#e2e8f0] mb-2">
                {key} Return — Quadrant Medians (SPY benchmark: {(pd.benchmark * 100).toFixed(0)}%)
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(pd.quadrantMedians).map(([qk, qv]) => {
                  const excess = qv.median !== null ? qv.median - pd.benchmark : null;
                  return (
                    <div key={qk} className="rounded-lg p-3 bg-[#0d1117] border border-[#1e293b]">
                      <div className="text-[9px] text-[#e2e8f0] uppercase tracking-wider mb-1">{qv.label}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold tabular-nums"
                          style={{ color: excess !== null && excess > 0 ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>
                          {qv.median !== null ? `${(qv.median * 100).toFixed(0)}%` : 'N/A'}
                        </span>
                        {excess !== null && (
                          <span className="text-[10px] tabular-nums"
                            style={{ color: excess > 0 ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>
                            {excess >= 0 ? '+' : ''}{(excess * 100).toFixed(0)}pp vs SPY
                          </span>
                        )}
                        <span className="text-[9px] text-[#64748b]">n={qv.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* RSI Backtest section removed 2026-04-24 — RSI technical indicator is no longer part of the model
            (timeframe selector, oversold/overbought signals, and technical Z-axis all deprecated in favour of
            the Momentum pillar). A Momentum-overlay backtest could be rebuilt once the pipeline emits
            momentumBacktest in place of rsiBacktest. */}

        {/* Quintile Chart */}
        {hasVintageData && (
        <div className="rounded-xl p-4 bg-[#0d1117] border border-[#1e293b]">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0] mb-3">
            Quintile Forward 1-Year Returns (Q1 = Best Scored)
          </div>
          <QuintileChart
            quintileReturns={quintileReturns}
            avgQuintileReturns={avgQuintileReturns}
            vintages={data.vintages}
          />
        </div>
        )}

        {/* Pillar Weight Sliders */}
        <div className="rounded-xl p-4 bg-[#0d1117] border border-[#1e293b]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0]">
              Pillar Weights
            </span>
            {!isDefaultWeights && (
              <button
                onClick={() => setWeights(DEFAULT_WEIGHTS)}
                className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
              >
                Reset
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            <WeightSlider label="Profitability" value={weights.profitability} onChange={v => setWeights({ ...weights, profitability: v })} />
            <WeightSlider label="Growth" value={weights.growth} onChange={v => setWeights({ ...weights, growth: v })} />
            <WeightSlider label="Safety" value={weights.safety} onChange={v => setWeights({ ...weights, safety: v })} />
            <WeightSlider label="Shr. Returns" value={weights.shareholderReturns} onChange={v => setWeights({ ...weights, shareholderReturns: v })} />
            <WeightSlider label="Valuation" value={weights.valuation} onChange={v => setWeights({ ...weights, valuation: v })} />
          </div>
        </div>

        {/* IC Heatmap */}
        {hasVintageData && (
        <div className="rounded-xl p-4 bg-[#0d1117] border border-[#1e293b]">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0] mb-3">
            Information Coefficients (Spearman Rank Correlation with Forward Returns)
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ fontFamily: 'var(--font-mono), monospace' }}>
              <thead>
                <tr>
                  <th className="text-left text-[9px] text-[#e2e8f0] uppercase tracking-wider px-2 py-1.5 border-b border-[#1e293b]">Factor</th>
                  {data.vintages.map(v => (
                    <th key={v} className="text-center text-[9px] text-[#e2e8f0] uppercase tracking-wider px-2 py-1.5 border-b border-[#1e293b] w-[65px]">{v}</th>
                  ))}
                  <th className="text-center text-[9px] text-[#e2e8f0] uppercase tracking-wider px-2 py-1.5 border-b border-[#1e293b] w-[65px] font-bold">Avg</th>
                </tr>
              </thead>
              <tbody>
                {/* Quality factors by pillar */}
                {allPillars.map(([pillar, factors]) => (
                  <React.Fragment key={pillar}>
                    <tr key={`hdr-${pillar}`}>
                      <td colSpan={data.vintages.length + 2}
                        className="text-[8px] font-bold uppercase tracking-widest text-[#94a3b8] px-2 pt-3 pb-1">
                        {pillar}
                      </td>
                    </tr>
                    {factors.map(({ key }) => (
                      <tr key={key}>
                        <td className="text-[10px] text-[#94a3b8] px-2 py-0.5">{factorLabel(key)}</td>
                        {data.vintages.map(v => (
                          <td key={v} className="text-center text-[9px] px-1 py-0.5"
                            style={{ backgroundColor: icColor(data.factorICs[key]?.[v] ?? null), color: icTextColor(data.factorICs[key]?.[v] ?? null) }}>
                            {icText(data.factorICs[key]?.[v] ?? null)}
                          </td>
                        ))}
                        <td className="text-center text-[9px] px-1 py-0.5 font-semibold"
                          style={{ backgroundColor: icColor(data.avgFactorICs[key] ?? null), color: icTextColor(data.avgFactorICs[key] ?? null) }}>
                          {icText(data.avgFactorICs[key] ?? null)}
                        </td>
                      </tr>
                    ))}
                    {/* Pillar score IC */}
                    <tr key={`pillar-${pillar}`} className="border-t border-[#1e293b]/50">
                      <td className="text-[10px] text-[#94a3b8] font-semibold px-2 py-0.5 italic">{pillar} pillar</td>
                      {data.vintages.map(v => (
                        <td key={v} className="text-center text-[9px] px-1 py-0.5 font-semibold"
                          style={{ backgroundColor: icColor(data.pillarICs[pillar]?.[v] ?? null), color: icTextColor(data.pillarICs[pillar]?.[v] ?? null) }}>
                          {icText(data.pillarICs[pillar]?.[v] ?? null)}
                        </td>
                      ))}
                      <td className="text-center text-[9px] px-1 py-0.5 font-bold"
                        style={{ backgroundColor: icColor(data.avgPillarICs[pillar] ?? null), color: icTextColor(data.avgPillarICs[pillar] ?? null) }}>
                        {icText(data.avgPillarICs[pillar] ?? null)}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}

                {/* Valuation factors */}
                <tr>
                  <td colSpan={data.vintages.length + 2}
                    className="text-[8px] font-bold uppercase tracking-widest text-[#94a3b8] px-2 pt-3 pb-1">
                    Valuation
                  </td>
                </tr>
                {VALUATION_FACTORS.map(({ key }) => (
                  <tr key={key}>
                    <td className="text-[10px] text-[#94a3b8] px-2 py-0.5">{factorLabel(key)}</td>
                    {data.vintages.map(v => (
                      <td key={v} className="text-center text-[9px] px-1 py-0.5"
                        style={{ backgroundColor: icColor(data.factorICs[key]?.[v] ?? null), color: icTextColor(data.factorICs[key]?.[v] ?? null) }}>
                        {icText(data.factorICs[key]?.[v] ?? null)}
                      </td>
                    ))}
                    <td className="text-center text-[9px] px-1 py-0.5 font-semibold"
                      style={{ backgroundColor: icColor(data.avgFactorICs[key] ?? null), color: icTextColor(data.avgFactorICs[key] ?? null) }}>
                      {icText(data.avgFactorICs[key] ?? null)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-[#1e293b]/50">
                  <td className="text-[10px] text-[#94a3b8] font-semibold px-2 py-0.5 italic">valuation pillar</td>
                  {data.vintages.map(v => (
                    <td key={v} className="text-center text-[9px] px-1 py-0.5 font-semibold"
                      style={{ backgroundColor: icColor(data.pillarICs['valuation']?.[v] ?? null), color: icTextColor(data.pillarICs['valuation']?.[v] ?? null) }}>
                      {icText(data.pillarICs['valuation']?.[v] ?? null)}
                    </td>
                  ))}
                  <td className="text-center text-[9px] px-1 py-0.5 font-bold"
                    style={{ backgroundColor: icColor(data.avgPillarICs['valuation'] ?? null), color: icTextColor(data.avgPillarICs['valuation'] ?? null) }}>
                    {icText(data.avgPillarICs['valuation'] ?? null)}
                  </td>
                </tr>

                {/* Composite scores */}
                <tr>
                  <td colSpan={data.vintages.length + 2}
                    className="text-[8px] font-bold uppercase tracking-widest text-[#94a3b8] px-2 pt-3 pb-1">
                    Composite
                  </td>
                </tr>
                <tr className="border-t border-[#1e293b]">
                  <td className="text-[10px] text-[#94a3b8] font-bold px-2 py-1">Quality Score</td>
                  {data.vintages.map(v => (
                    <td key={v} className="text-center text-[9px] px-1 py-1 font-bold"
                      style={{ backgroundColor: icColor(data.pillarICs['quality']?.[v] ?? null), color: icTextColor(data.pillarICs['quality']?.[v] ?? null) }}>
                      {icText(data.pillarICs['quality']?.[v] ?? null)}
                    </td>
                  ))}
                  <td className="text-center text-[9px] px-1 py-1 font-bold"
                    style={{ backgroundColor: icColor(data.avgPillarICs['quality'] ?? null), color: icTextColor(data.avgPillarICs['quality'] ?? null) }}>
                    {icText(data.avgPillarICs['quality'] ?? null)}
                  </td>
                </tr>
                <tr>
                  <td className="text-[10px] text-[#94a3b8] font-bold px-2 py-1">Quality + Value</td>
                  {data.vintages.map(v => (
                    <td key={v} className="text-center text-[9px] px-1 py-1 font-bold"
                      style={{ backgroundColor: icColor(data.compositeICs[v] ?? null), color: icTextColor(data.compositeICs[v] ?? null) }}>
                      {icText(data.compositeICs[v] ?? null)}
                    </td>
                  ))}
                  <td className="text-center text-[9px] px-1 py-1 font-bold"
                    style={{ backgroundColor: icColor(data.avgCompositeIC ?? null), color: icTextColor(data.avgCompositeIC ?? null) }}>
                    {icText(data.avgCompositeIC ?? null)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
